import { z } from 'zod';
import { Category1 } from './taxonomy.js';
import { CursorQuery } from './common/pagination.js';
/**
 * Blocking (FR-NET-11, AC-NET-08, AC-GL-18). A block is bidirectional in
 * effect: both members disappear from each other's feeds, lists, search and
 * profiles. Unblock restores visibility only — never any prior relationship
 * (AC-NET-08.3).
 */
export const BlockState = z.object({
    userId: z.string(),
    blocked: z.boolean(),
});
export const BlockedUserView = z.object({
    userId: z.string(),
    /** Null when the member never completed profile setup. */
    displayName: z.string().nullable(),
    blockedAt: z.coerce.date(),
});
/**
 * Connections (FR-NET-01…06, AC-NET-02/03): mutual, accepted. States seen by
 * the requester's row: none → requested → connected; the recipient sees an
 * incoming request. A decline returns the row to none and the requester is
 * NOT told (AC-NET-02.3).
 */
export const ConnectionState = z.enum(['none', 'requested', 'incoming', 'connected']);
export const ConnectionStateView = z.object({
    userId: z.string(),
    state: ConnectionState,
    /**
     * The areas the connection — or the pending request — was made under.
     * Always the REAL ones, even when `state` has been narrowed by a filter, so
     * a caller is never told less than the truth about the relationship.
     *
     * Defaulted, so this is additive: a client written before it existed keeps
     * parsing the response unchanged.
     */
    areasOfInterest: z.array(Category1).default([]),
});
/**
 * Which areas a connect request is made under (FR-NET-13). Deduplicated on
 * the way in so a chip tapped twice cannot be stored twice.
 */
export const AreasOfInterest = z
    .array(Category1)
    .max(40)
    .transform((v) => [...new Set(v)]);
/**
 * Body of `connect`. Every field is optional and the body itself defaults,
 * so a client that predates this field keeps connecting with no body at all
 * — the whole point of adding it this way rather than as a required input.
 *
 * `[]` means UNSTATED, never "none of them": a connection made before the
 * field existed, or by a client that does not send it, must not read as a
 * deliberate choice of no areas. Filters therefore skip those rows rather
 * than treating them as an empty set.
 */
export const ConnectInput = z
    .object({ areasOfInterest: AreasOfInterest.default([]) })
    .default({ areasOfInterest: [] });
export const ConnectionRow = z.object({
    userId: z.string(),
    displayName: z.string().nullable(),
    /**
     * Profile picture, ABSENT when the member has none — not null, not a
     * placeholder the API invented. Optional in both directions, which is what
     * makes this additive: a client written before it existed ignores the extra
     * key, and one written after it still parses rows served by an API that
     * predates it. What an avatar-less row renders is the app's decision.
     */
    avatarUrl: z.string().optional(),
    jobTitle: z.string().nullable(),
    company: z.string().nullable(),
    /** "4 Mutual Connects" on every person row (FR-NET-05). */
    mutualCount: z.number().int().nonnegative(),
    since: z.coerce.date(),
    /**
     * As declared by whoever SENT the request; `[]` when unstated.
     *
     * Defaulted rather than required so the field is optional on the WIRE while
     * still always present to callers. The api-client validates responses, so a
     * required field here would mean a newly built app rejects every row served
     * by an API that predates it — turning a purely additive change into a
     * deploy-order constraint.
     */
    areasOfInterest: z.array(Category1).default([]),
});
/**
 * The area-of-interest filter sheet on My Connections (FR-NET-13). Omit for
 * All. Repeatable — `?areasOfInterest=political&areasOfInterest=business` —
 * and a single value is accepted bare, because Fastify's parser yields a
 * string for one occurrence and an array for several.
 */
/**
 * Narrows `state` to one or more areas: "am I connected to this person IN
 * Entertainment?" rather than "am I connected to them at all". A relationship
 * that exists but under none of the named areas answers `none`, which is what
 * a Category-1 tab needs in order to offer Connect.
 *
 * `areasOfInterest` in the RESPONSE is unaffected by this and still lists the
 * real areas, so the narrowing hides nothing — it only answers the narrower
 * question that was asked.
 */
export const ConnectionStateQuery = z.object({
    areasOfInterest: z
        .union([Category1, z.array(Category1)])
        .transform((v) => (Array.isArray(v) ? v : [v]))
        .pipe(AreasOfInterest)
        .optional(),
});
export const ConnectionQuery = CursorQuery.extend({
    areasOfInterest: z
        .union([Category1, z.array(Category1)])
        .transform((v) => (Array.isArray(v) ? v : [v]))
        .pipe(AreasOfInterest)
        .optional(),
});
/**
 * Supporting (FR-NET-04, AC-NET-04): one-way, no approval, recorded against
 * a Category 1 so the Support-type filter sheet works (FR-NET-12).
 */
export const SupportInput = z.object({ category1: Category1 });
/**
 * Which area to withdraw. Omitted means ALL of them, which is what `unsupport`
 * did when a person could only be supported in one area — so a client that
 * predates this still fully unsupports, exactly as before.
 */
export const UnsupportQuery = z.object({ category1: Category1.optional() });
export const SupportState = z.object({
    userId: z.string(),
    supporting: z.boolean(),
    /**
     * DEPRECATED — the most recently added area, kept so clients written against
     * the one-area-per-person model keep rendering. Read `categories` instead:
     * support is per area, so this cannot represent someone backed in both
     * Political and Business and will silently under-report them.
     */
    category1: Category1.nullable(),
    /** Every area this support covers. Empty exactly when `supporting` is false. */
    categories: z.array(Category1).default([]),
});
export const SupporterRow = z.object({
    userId: z.string(),
    displayName: z.string().nullable(),
    /** Profile picture; absent — never null — when the member has none, as on `ConnectionRow`. */
    avatarUrl: z.string().optional(),
    category1: Category1,
    mutualCount: z.number().int().nonnegative(),
    since: z.coerce.date(),
});
export const SupporterQuery = CursorQuery.extend({
    /** Support-type filter (FR-NET-12): omit for All. */
    category1: Category1.optional(),
});
//# sourceMappingURL=network.js.map